import * as React from 'react'

import { makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { CellMeasurerCache } from 'react-virtualized'

import {
  IFormMaterial,
  IMeasure,
  IPermitTypeField,
  LocationType,
  PermitFieldType,
} from '~/client/graph'
import BaseCompactPopup from '~/client/src/shared/components/BaseCompactPopup/BaseCompactPopup'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import { Loader } from '~/client/src/shared/components/Loader'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import { materialPermitFieldTypes } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import InitialState from '~/client/src/shared/stores/InitialState'
import { getLocationTypeDisplayName } from '~/client/src/shared/types/IHierarchyParent'
import { NOOP } from '~/client/src/shared/utils/noop'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import FormTypeSelectionForm from '../../../../FormTypeSelectionForm'
import SitePermitCreationFormStore, {
  FormDropdownType,
  IFormDropdownOption,
} from '../../../../WorkflowCreationForm.store'
import FormCompanyPicker from './components/FormCompanyPicker'
import FormDropdownOptionsList from './components/FormDropdownOptionsList'
import FormEquipmentPicker from './components/FormEquipmentPicker'
import FormLocationResourcePicker from './components/FormLocationResourcePicker'
import FormMaterialResourcePicker from './components/FormMaterialResourcePicker'
import FormUserPicker from './components/FormUserPicker'
import FormWorkflowPicker from './components/FormWorkflowPicker'
import FormDropdownOptionsModalStore from './FormDropdownOptionsModal.store'

import './FormDropdownOptionsModal.scss'

// localization: translated

interface IProps {
  store: SitePermitCreationFormStore

  isShown: boolean
  isPreviewMode: boolean
  allowedTypeIds?: string[]

  state?: InitialState
}

const DEFAULT_ROW_HEIGHT = 48

const isCorrectLocationType = (locationType: LocationType): boolean =>
  locationType && locationType !== LocationType.OffloadingEquipment

@inject('state')
@observer
export default class FormDropdownOptionsModal extends React.Component<IProps> {
  private readonly cellMeasurerCache: CellMeasurerCache = null
  private readonly selectionStore: FormDropdownOptionsModalStore
  private resourceCreateFn: { current: (() => void) | null } = {
    current: null,
  }

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)

    this.cellMeasurerCache = new CellMeasurerCache({
      defaultHeight: DEFAULT_ROW_HEIGHT,
      fixedWidth: true,
    })

    this.selectionStore = new FormDropdownOptionsModalStore(
      this.props.store.selectedField,
      this.props.store.selectedFieldValues,
    )
  }

  public componentDidUpdate(prevProps: IProps): void {
    if (prevProps.isShown !== this.props.isShown) {
      this.selectionStore.reset(
        this.props.store.selectedField,
        this.props.store.selectedFieldValues,
      )
    }
  }

  public render() {
    const { store, isShown } = this.props
    const onOutsideClickHandler = isShown ? this.onModalClose : NOOP

    return (
      <MenuCloser
        className={classList({
          'form-dropdown-options-modal': true,
          'is-shown': isShown,
          'workflow-picker-mode':
            this.isWorkflowPickerShown || this.isResourcePickerShown,
          'type-selector-mode': this.isTypeSelectorShown,
        })}
        closeMenu={onOutsideClickHandler}
      >
        <BaseCompactPopup
          childrenClassName={classList({
            'full-height overflow-hidden': true,
            'tw:flex tw:flex-col': this.isListSurfaceShown,
          })}
          title={this.isListSurfaceShown ? undefined : this.title}
          isShown={isShown}
          onHide={this.onModalClose}
          shouldIncludeSearch={this.isSearchBarShown}
          searchPlaceholder={this.searchPlaceholder}
          searchValue={store.modalSearchValue}
          onSearchValueChange={this.onSearchValueChange}
          onSearchValueClear={this.clearSearchValue}
          actionButtonTitle={
            this.isApplyButtonShown && Localization.translator.apply
          }
          onActionButtonClick={this.handleSelectionApply.bind(this)}
          isActionButtonEnabled
        >
          {this.isListSurfaceShown && (
            <ListSelector.Title
              title={this.title}
              onClose={this.onModalClose}
            />
          )}
          <div
            className={
              this.isListSurfaceShown
                ? 'tw:flex tw:min-h-0 tw:flex-1 tw:flex-col'
                : 'col full-height'
            }
          >
            {this.isLoading ? <Loader /> : this.renderOptions()}
          </div>
        </BaseCompactPopup>
      </MenuCloser>
    )
  }

  private renderOptions(): JSX.Element {
    if (!this.props.isShown) return null

    const { store } = this.props

    if (this.isEquipmentPickerShown) {
      return (
        <FormEquipmentPicker
          store={store}
          projectId={this.props.state?.activeProject?.id}
          startCreatingRef={this.resourceCreateFn}
          onClose={this.onModalClose}
          startDate={store.editablePermit?.startDate ?? null}
          endDate={store.editablePermit?.endDate ?? null}
          permitTypeId={store.editablePermit?.typeId ?? null}
          permitId={store.editablePermit?.id ?? null}
          isAdminOrFormsMaster={store.isAdminOrFormsMaster}
          timePeriods={store.locationTimePeriods}
        />
      )
    }
    if (this.isMaterialResourcePickerShown) {
      return (
        <FormMaterialResourcePicker
          store={store}
          projectId={this.props.state?.activeProject?.id}
          startCreatingRef={this.resourceCreateFn}
          onClose={this.onModalClose}
        />
      )
    }
    if (this.isLocationPickerShown) {
      return (
        <FormLocationResourcePicker
          store={store}
          projectId={this.props.state?.activeProject?.id}
          startCreatingRef={this.resourceCreateFn}
          onClose={this.onModalClose}
        />
      )
    }
    if (this.isUserPickerShown) {
      return (
        <FormUserPicker
          store={store}
          selectionStore={this.selectionStore}
          onInstantApply={this.handleInstantUserApply}
          onBatchSelect={this.handleInstantBatchSelect}
        />
      )
    }
    if (this.isCompanyPickerShown) {
      return <FormCompanyPicker store={store} />
    }
    if (this.isWorkflowPickerShown) {
      return (
        <FormWorkflowPicker
          store={store}
          isPreviewMode={this.props.isPreviewMode}
          onClose={this.onModalClose}
        />
      )
    }
    if (this.isTypeSelectorShown) {
      const { hideFormTypeSelector } = this.props.store

      return (
        <FormTypeSelectionForm
          onSelect={this.handlePermitTypeChanged}
          onClose={hideFormTypeSelector}
          hideHeader
          allowedTypeIds={this.props.allowedTypeIds}
          selectedTypeId={this.props.store.editablePermit?.typeId}
        />
      )
    }
    return (
      <FormDropdownOptionsList
        cellMeasurerCache={this.cellMeasurerCache}
        dropdownOptions={this.dropdownOptions}
        scrollToIndex={this.selectedOptionIndex}
        isMultiple={this.isMultiple}
        onOptionClick={this.optionClickHandler}
      />
    )
  }

  private isOptionSelected = (option: IFormDropdownOption): boolean => {
    const { selectedFieldValues, selectedFieldDropdownType, template } =
      this.props.store
    const selectedFieldValue = selectedFieldValues[this.selectedFieldIndex]

    switch (selectedFieldDropdownType) {
      case FormDropdownType.MATERIAL:
        return (
          (selectedFieldValue as IFormMaterial)?.materialId === option.value
        )
      case FormDropdownType.PROCUREMENT_ID:
        return (
          (selectedFieldValue as IFormMaterial)?.procurementId === option.value
        )
      case FormDropdownType.DEFAULT:
        return this.isMeasure
          ? (selectedFieldValues as IMeasure[]).some(
              v => v?.units === option.value,
            )
          : this.selectedValues.includes(option.value)
      case FormDropdownType.FORM_TYPE:
        return template?.id === option.value

      default:
        return false
    }
  }

  private onModalClose = () => {
    const { resetSelectedField, hideFormTypeSelector } = this.props.store
    resetSelectedField()
    hideFormTypeSelector()
    this.clearSearchValue()

    this.clearCellMeasurerCache()
  }

  private onSearchValueChange = (newValue: string) => {
    this.props.store.updateSearchValue(newValue)
    this.clearCellMeasurerCache()
  }

  private clearSearchValue = () => {
    this.props.store.updateSearchValue(EMPTY_STRING)
    this.clearCellMeasurerCache()
  }

  private handleApply(value: string) {
    const {
      changeSelectedFieldValue,
      isTypeSelectorShown,
      hideFormTypeSelector,
      onPermitTypeChanged,
    } = this.props.store

    if (isTypeSelectorShown) {
      onPermitTypeChanged(value)
      hideFormTypeSelector()
    } else {
      changeSelectedFieldValue(value)
    }

    this.clearSearchValue()
    this.clearCellMeasurerCache()
  }

  private handleSelectionApply() {
    const { changeSelectedFieldValue } = this.props.store
    changeSelectedFieldValue(this.selectedValues)

    this.clearSearchValue()
    this.clearCellMeasurerCache()
  }

  private handleInstantUserApply = (userId: string) => {
    this.selectionStore.onOptionClick(userId)
    // Single-value: pick = apply + close. Multi-value: toggle tentatively and
    // commit via the Apply button (X cancels) — a set needs an explicit confirm.
    if (!this.isMultiple) {
      this.handleSelectionApply()
    }
  }

  private handleInstantBatchSelect = (userIds: string[]) => {
    // Multi-value group select — tentative until Apply.
    this.selectionStore.setSelectedValues(userIds)
  }

  private optionClickHandler = (value: string) => {
    if (this.isSelectionUsed) {
      return this.selectionStore.onOptionClick(value)
    }

    return this.handleApply(value)
  }

  private clearCellMeasurerCache = () => {
    this.cellMeasurerCache.clearAll()
  }

  private handlePermitTypeChanged = (formTypeId: string) => {
    const { onPermitTypeChanged, hideFormTypeSelector } = this.props.store
    onPermitTypeChanged(formTypeId)
    hideFormTypeSelector()
  }

  private get selectedFieldName(): string {
    const { selectedFieldDropdownType, selectedSubField } = this.props.store
    switch (selectedFieldDropdownType) {
      case FormDropdownType.PROCUREMENT_ID:
        return Localization.translator.procurementID
      case FormDropdownType.FORM_TYPE:
        return Localization.translator.formType
      default:
        return selectedSubField?.caption || this.selectedField?.caption
    }
  }

  private get title(): string {
    if (this.isWorkflowPickerShown) {
      return this.formattedFieldCaption
    }
    return `${Localization.translator.select} ${this.formattedFieldCaption}`
  }

  private get isLoading(): boolean {
    const { loading } = this.props.state

    if (this.isLocationPickerShown) {
      return loading.get(e.LOAD_LOCATIONS_AND_LISTEN_CHANGES)
    }
    if (this.isCompanyPickerShown) {
      return loading.get(e.LOAD_AND_LISTEN_TO_COMPANIES)
    }
    if (this.isUserPickerShown) {
      return loading.get(e.GET_PROJECT_MEMBERS)
    }
    if (this.isWorkflowPickerShown) {
      return (
        loading.get(e.LOAD_AND_LISTEN_TO_WORKFLOW_TYPES) ||
        loading.get(e.GET_DEFAULT_WORKFLOW_TYPES)
      )
    }
    if (this.isTypeSelectorShown) {
      return (
        loading.get(e.LOAD_AND_LISTEN_TO_WORKFLOW_TYPES) ||
        loading.get(e.GET_DEFAULT_WORKFLOW_TYPES)
      )
    }
    if (
      this.props.store.selectedFieldDropdownType ===
        FormDropdownType.MATERIAL ||
      this.props.store.selectedFieldDropdownType ===
        FormDropdownType.PROCUREMENT_ID
    ) {
      return (
        loading.get(e.LOAD_AND_LISTEN_TO_MATERIALS) ||
        loading.get(e.LOAD_AND_LISTEN_TO_MATERIAL_CATEGORIES)
      )
    }

    return false
  }

  private get searchPlaceholder(): string {
    if (this.isLoading) {
      return `${Localization.translator.loading}...`
    }

    return Localization.translator.search
  }

  private get formattedFieldCaption(): string {
    const { selectedLocationType } = this.props.store
    if (
      this.isLocationPickerShown &&
      isCorrectLocationType(selectedLocationType)
    ) {
      return `${this.selectedFieldName} (${getLocationTypeDisplayName(
        selectedLocationType,
      )})`
    }
    return this.selectedFieldName
  }

  private get dropdownOptions(): IFormDropdownOption[] {
    return this.props.store.dropdownModalOptions.map(opt => ({
      ...opt,
      isSelected: this.isOptionSelected(opt),
    }))
  }

  private get selectedOptionIndex(): number {
    return this.dropdownOptions.findIndex(opt => opt.isSelected)
  }

  private get selectedField(): IPermitTypeField {
    return this.props.store.selectedField
  }

  private get selectedFieldIndex(): number {
    return this.props.store.selectedFieldIndex
  }

  private get isMeasure(): boolean {
    return this.selectedField?.type === PermitFieldType.Measure
  }

  private get isMultiple(): boolean {
    return (
      !materialPermitFieldTypes.includes(this.selectedField?.type) &&
      this.selectedField?.isMultiple
    )
  }

  private get isSearchBarShown(): boolean {
    return (
      !this.isResourcePickerShown &&
      !this.isCompanyPickerShown &&
      !this.isUserPickerShown &&
      !this.isTypeSelectorShown &&
      !this.isWorkflowPickerShown
    )
  }

  private get isListSurfaceShown(): boolean {
    return (
      this.isResourcePickerShown ||
      this.isWorkflowPickerShown ||
      this.isTypeSelectorShown
    )
  }

  private get isResourcePickerShown(): boolean {
    return (
      this.isEquipmentPickerShown ||
      this.isMaterialResourcePickerShown ||
      this.isLocationPickerShown
    )
  }

  private get isEquipmentPickerShown(): boolean {
    return this.props.store.isEquipmentPickerDisplayed
  }

  private get isMaterialResourcePickerShown(): boolean {
    return this.props.store.isMaterialResourcePickerDisplayed
  }

  private get isLocationPickerShown(): boolean {
    return this.props.store.isLocationPickerDisplayed
  }

  private get isCompanyPickerShown(): boolean {
    return this.props.store.isCompanyPickerDisplayed
  }

  private get isWorkflowPickerShown(): boolean {
    return this.props.store.isWorkflowPickerDisplayed
  }

  private get isUserPickerShown(): boolean {
    return this.props.store.isUserPickerDisplayed
  }

  private get isSelectPickerShown(): boolean {
    return this.selectedField?.type === PermitFieldType.Select
  }

  private get isTypeSelectorShown(): boolean {
    return this.props.store.isTypeSelectorShown
  }

  private get selectedValues(): string[] {
    return this.selectionStore.values
  }

  private get isApplyButtonShown(): boolean {
    // Single-value user fields apply instantly on the pick (no Apply); multi
    // stays open and commits via Apply. Select pickers keep Apply too.
    return (
      this.isSelectPickerShown || (this.isUserPickerShown && this.isMultiple)
    )
  }

  private get isSelectionUsed(): boolean {
    return this.isUserPickerShown || this.isSelectPickerShown
  }
}
